import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/security/auth.service';
import { SecureTokenService } from '../services/security/secure-token.service';

interface RefreshTokenResponse {
  access_token: string;
}

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);
let eulaModalOpen = false;

function addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

function checkEulaStatus(token: string, authService: AuthService): void {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return;
    const payload = JSON.parse(atob(parts[1]));

    const needsEulaAcceptance = Number.isInteger(payload.eula_accepted_version) &&
      Number.isInteger(payload.eula_current_version) &&
      (payload.eula_accepted_version < payload.eula_current_version);

    if (needsEulaAcceptance && !eulaModalOpen) {
      eulaModalOpen = true;
      authService.triggerEulaModal();
    }
  } catch {
    // Ignore JWT decode errors
  }
}

function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((token: RefreshTokenResponse) => {
        isRefreshing = false;
        refreshTokenSubject.next(token.access_token);
        return next(addToken(request, token.access_token));
      }),
      catchError((err) => {
        isRefreshing = false;
        // Clear tokens and redirect to login without causing loops
        authService.clearTokens();
        router.navigate(['/login']);
        return throwError(() => err);
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter((token): token is string => token != null),
      take(1),
      switchMap(jwt => next(addToken(request, jwt)))
    );
  }
}

/**
 * Object storage signs its own URLs and answers preflight with a wildcard
 * origin, which a credentialed request may not accept. Sending our token
 * there also gives the request two auth mechanisms, which storage refuses.
 */
function isExternalStorageUrl(url: string): boolean {
  return url.includes('.amazonaws.com') || url.includes('.cloudfront.net');
}

export const jwtInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const secureTokenService = inject(SecureTokenService);
  const router = inject(Router);

  if (isExternalStorageUrl(request.url)) {
    return next(request);
  }

  // Skip auth for auth endpoints to prevent loops, but include credentials for cookies
  if (request.url.includes('/auth/login') || request.url.includes('/auth/refresh')) {
    return next(request.clone({ withCredentials: true }));
  }

  // Add authorization header from secure storage
  const accessToken = secureTokenService.getAccessToken();
  let req = request;
  if (accessToken) {
    req = addToken(request, accessToken);
    // Check EULA status from JWT token
    checkEulaStatus(accessToken, authService);
  }

  // Always include credentials for cookie-based refresh tokens
  req = req.clone({ withCredentials: true });

  return next(req).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handle401Error(req, next, authService, router);
      }
      return throwError(() => error);
    })
  );
};
